/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useEffect } from 'react';
import { IconButton } from "@/subframe/components/IconButton";
import { DialogLayout } from "@/subframe/layouts/DialogLayout";
import { Tooltip } from "@/subframe/components/Tooltip";
import { Button } from "@/subframe/components/Button";
import SubframeCore from "@subframe/core";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { handleMediaSend } from "../../redux/reducers/mediaMessageSlice";
import ScheduleMessage from "../templates/ScheduleTemplateMenu";
import type { SchedulePayload } from "../templates/ScheduleTemplateMenu";
import { addNotification } from "../../redux/reducers/notificationSlice";
import { NotificationIcon, NotificationType } from "../../Utils/notificationEnums";
import moment from "moment";
import VoiceMessage from './VoiceMessage';
import { getAuthToken } from "../../Utils/authToken";
import MicRecorder from 'mic-recorder-to-mp3-fixed';

interface VoiceRecorderProps {
  currentRecipient: any;
  onClose?: () => void;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ currentRecipient, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const system_user_id = useSelector((state: RootState) => state.auth.system_user_id);
  const userDisplayName = useSelector((state: RootState) => state.auth.userDisplayName);
  const isMediaUploading = useSelector((state: RootState) => state.mediaMessage.isLoading);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isScheduleMessagePopupOpen, setIsScheduleMessagePopupOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<any>(null);

  const recorderRef = useRef<InstanceType<typeof MicRecorder> | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (recorderRef.current && isRecording) {
        recorderRef.current.stop();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [audioUrl, isRecording]);

  const getMicErrorMessage = (err: unknown): string => {
    const name = err && typeof err === 'object' && 'name' in err ? (err as { name: string }).name : '';
    const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : '';

    if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || message.includes('Permission denied')) {
      return 'Microphone access was denied or blocked. Use the lock or camera icon in the address bar → Site settings → set Microphone to Allow.';
    }
    if (name === 'NotFoundError' || message.includes('not found')) {
      return 'No microphone was found. Connect a microphone and try again.';
    }
    if (name === 'SecurityError' || message.includes('secure')) {
      return 'Microphone is only available on secure pages (HTTPS). Open this app via https:// or localhost.';
    }
    if (name === 'NotReadableError' || name === 'AbortError') {
      return 'Microphone is in use by another app or tab. Close other apps using the mic and try again.';
    }
    // AudioContext / script processor / encoding can fail even when mic is allowed
    if (name === 'InvalidStateError' || name === 'NotSupportedError' || message.includes('AudioContext') || message.includes('suspended')) {
      return 'Audio couldn\'t start. Click Start Recording again, or refresh the page and try once. If it keeps failing, try another browser (e.g. Chrome).';
    }
    // Show the real error when we don't recognise it so user isn't told "allow mic" when they already did
    if (name || message) {
      return `Recording failed: ${name ? `${name}. ` : ''}${message || 'Unknown error'}. Try refreshing and clicking Start again, or use Chrome.`;
    }
    return 'Recording couldn\'t start. Try refreshing the page and click Start Recording again, or use another browser.';
  };

  const startRecording = (): void => {
    if (isRecording) return;

    if (typeof navigator !== 'undefined' && !navigator.mediaDevices?.getUserMedia) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Microphone not supported",
          message: "Your browser or page doesn't support microphone access. Use HTTPS or a modern browser.",
          icon: NotificationIcon.ERROR,
        })
      );
      return;
    }

    // Unlock audio for the page (some browsers keep AudioContext suspended until resume() in a user gesture)
    const unlockAudio = (): Promise<void> => {
      const Ctx = typeof window !== 'undefined' && (window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
      if (!Ctx) return Promise.resolve();
      const ctx = new Ctx();
      if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
        return ctx.resume().then(() => { ctx.close(); });
      }
      ctx.close();
      return Promise.resolve();
    };

    const recorder = new MicRecorder({ bitRate: 128 });
    recorderRef.current = recorder;
    unlockAudio()
      .then(() => recorder.start())
      .then(() => {
        setRecordingTime(0);
        setIsRecording(true);
        timerRef.current = setInterval(() => {
          setRecordingTime((prev) => prev + 1);
        }, 1000);
      })
      .catch((err) => {
        recorderRef.current = null;
        console.error('Voice recording start failed:', err);
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.ERROR,
            title: "Recording couldn't start",
            message: getMicErrorMessage(err),
            icon: NotificationIcon.ERROR,
          })
        );
      });
  };

  const stopRecording = () => {
    if (!recorderRef.current || !isRecording) return;

    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const recorder = recorderRef.current;
    recorderRef.current = null;

    recorder
      .stop()
      .getMp3()
      .then(([, blob]: [ArrayBuffer, Blob]) => {
        if (blob && blob.size > 0) {
          setAudioBlob(blob);
          setAudioUrl(URL.createObjectURL(blob));
          setShowPreview(true);
        }
      })
      .catch(() => {
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.ERROR,
            title: "Recording Failed",
            message: "Could not encode audio. Please try again.",
            icon: NotificationIcon.ERROR,
          })
        );
      });
  };
  
  const uploadVoiceMessage = async () => {
    if (!audioBlob) return;

    try {
      setIsUploading(true);
      setUploadError(null);
      const authToken = getAuthToken();
      
      const formData = new FormData();
      // Use a timestamp for unique file naming
      formData.append('file', audioBlob, `voice_message_${Date.now()}.mp3`);
      
      const response = await fetch('https://api.insightssystem.com/api:bVXsw_FD/upload_file_by_attachment', {
        method: 'POST',
        headers: {
          'Accept': '*/*',
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        setUploadedFile(result);
      } else {
        setUploadError(result.message || result.error || 'Upload failed');
        
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.ERROR,
            title: "WhatsApp Voice Message Validation Failed",
            message: result.message || "File doesn't meet WhatsApp requirements",
            icon: NotificationIcon.ERROR,
          })
        );
      }
    } catch (error: any) {
      setUploadError('Failed to upload voice message. Please try again.');
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Upload Failed",
          message: error.message || "Something went wrong during upload.",
          icon: NotificationIcon.ERROR,
        })
      );
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (audioBlob && showPreview) {
      uploadVoiceMessage();
    }
  }, [audioBlob, showPreview]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const handleSend = async (schedulePayload?: SchedulePayload) => {
    if (!uploadedFile || !system_user_id || !currentRecipient) return;

    const isScheduled = !!schedulePayload?.is_schedule;

    try {
      await dispatch(handleMediaSend({
        media_link: uploadedFile.file_url,
        type: "audio",
        caption: "",
        currentRecipient,
        senderUserId: system_user_id,
        senderDisplayName: userDisplayName,
        is_schedule: isScheduled,
        ...(schedulePayload || {}),
      })).unwrap();

      if (isScheduled) {
        let scheduleMessage = "Your voice message has been scheduled.";

        if (schedulePayload?.schedule_type === "specific") {
          const formattedLocalDateTime = schedulePayload.schedule_datetime_date
            ? moment(schedulePayload.schedule_datetime_date).local().format("MMM D, YYYY hh:mm A")
            : "selected date and time";
          const selectedTimezone = schedulePayload.time_zone || "your timezone";
          scheduleMessage = `Your voice message will be sent on ${formattedLocalDateTime} (${selectedTimezone}).`;
        } else if (schedulePayload?.schedule_type === "relative") {
          const unitValue = schedulePayload.unit_of_time_value || "";
          const unitName = schedulePayload.unit_of_time_name || "";
          scheduleMessage = `Your voice message will be sent after ${unitValue} ${unitName} from now.`;
        }

        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Voice Message Scheduled",
            message: scheduleMessage,
            icon: NotificationIcon.SUCCESS,
          })
        );
      } else {
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Voice Message Sent",
            message: "Your voice message has been sent successfully",
            icon: NotificationIcon.SUCCESS,
          })
        );
      }

      setShowPreview(false);
      setAudioBlob(null);
      setAudioUrl(null);
      setUploadedFile(null);
      setUploadError(null);
      
      if (onClose) {
        onClose();
      }
    } catch (error: any) {
      const errorTitle = isScheduled ? "Schedule Failed" : "Voice Message Not Sent";
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: errorTitle,
          message: error.message || "Something went wrong. Please try again.",
          icon: NotificationIcon.ERROR,
        })
      );

    }
  };

  const handleVoiceScheduleFromMenu = (schedulePayload: SchedulePayload) => {
    handleSend(schedulePayload);
  };

  const handleCancel = () => {
    setShowPreview(false);
    setAudioBlob(null);
    setAudioUrl(null);
    setUploadedFile(null);
    if (onClose) {
      onClose();
    }
  };

  // Recording button UI
  const renderRecordingButton = () => {
    if (isRecording) {
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-4">
            <div className="h-4 w-4 rounded-full bg-red-500 animate-pulse"></div>
            <span className="text-body font-body text-default-font">
              {formatTime(recordingTime)}
            </span>
          </div>
          <Button 
            variant="destructive-secondary"
            icon="FeatherSquare"
            onClick={stopRecording}
          >
            Stop Recording
          </Button>
        </div>
      );
    }
    
    return (
      <Button 
        variant="brand-primary"
        icon="FeatherMic"
        onClick={startRecording}
      >
        Start Recording
      </Button>
    );
  };

  return (
    <>
      <DialogLayout open={!showPreview && !audioUrl} onOpenChange={() => {
        if (onClose) onClose();
      }}>
        <div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-default-background px-6 py-6">
          <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-4">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Voice Message
              </span>
              <span className="text-body font-body text-subtext-color">
                Record a voice message to send
              </span>
              <span className="text-caption font-caption text-subtext-color mt-1">
                When you tap Start, your browser will ask for microphone access. If you don&apos;t see a prompt, use the lock or camera icon in the address bar → set Microphone to Allow.
              </span>
            </div>
            <IconButton
              icon="FeatherX"
              onClick={handleCancel}
            />
          </div>

          <div className="flex w-full h-40 items-center justify-center">
            {renderRecordingButton()}
          </div>
        </div>
      </DialogLayout>

      <DialogLayout open={showPreview && !!audioUrl} onOpenChange={() => setShowPreview(false)}>
        <div className="flex h-full w-full flex-col items-start gap-6 bg-default-background px-6 py-6">
          <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-4">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Voice Message Preview
              </span>
              <span className="text-body font-body text-subtext-color">
                {isUploading ? "Uploading..." : uploadedFile ? "Ready to send" : "Processing..."}
              </span>
            </div>
            <IconButton
              icon="FeatherX"
              onClick={handleCancel}
            />
          </div>

          <div className="relative w-full grow shrink-0 basis-0 flex-col items-center justify-center gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
            {isUploading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                  <div className="mt-2 text-neutral-600">Uploading...</div>
                </div>
              </div>
            ) : uploadError ? (
              <div className="text-center text-red-500 p-4">
                {uploadError}
              </div>
            ) : (
              <div className="flex w-full items-center justify-center p-4">
                {audioUrl && (
                  <VoiceMessage 
                    audioUrl={audioUrl} 
                    duration={recordingTime}
                    className="w-full max-w-md"
                    showVolumeControls={true}
                  />
                )}
              </div>
            )}
          </div>

          <div className="flex w-full grow shrink-0 basis-0 flex-col items-center gap-4">
            <div className="flex grow shrink-0 basis-0 items-center rounded-lg bg-brand-600">
              <SubframeCore.Tooltip.Provider>
                <SubframeCore.Tooltip.Root>
                  <SubframeCore.Tooltip.Trigger asChild={true}>
                    <IconButton
                      className="h-auto w-8 flex-none self-stretch"
                      variant="brand-primary"
                      icon="FeatherCalendarClock"
                      onClick={() => setIsScheduleMessagePopupOpen(true)}
                      disabled={!uploadedFile || isUploading}
                    />
                  </SubframeCore.Tooltip.Trigger>
                  <SubframeCore.Tooltip.Portal>
                    <SubframeCore.Tooltip.Content
                      side="bottom"
                      align="center"
                      sideOffset={4}
                      asChild={true}
                    >
                      <Tooltip>Schedule send</Tooltip>
                    </SubframeCore.Tooltip.Content>
                  </SubframeCore.Tooltip.Portal>
                </SubframeCore.Tooltip.Root>
              </SubframeCore.Tooltip.Provider>
              <div className="flex w-px flex-none flex-col items-center gap-2 self-stretch bg-brand-50" />
              <Button
                className="h-auto min-h-[36px] w-auto flex-none self-stretch"
                iconRight="FeatherSendHorizontal"
                onClick={() => handleSend()}
                disabled={isUploading || isMediaUploading || !uploadedFile}
                loading={isMediaUploading}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      </DialogLayout>

      <ScheduleMessage
        open={isScheduleMessagePopupOpen}
        setIsScheduleMessagePopupOpen={setIsScheduleMessagePopupOpen}
        onScheduleSubmit={handleVoiceScheduleFromMenu}
      />
    </>
  );
};

export default VoiceRecorder;